/**
 * Phase 106 (Plan 106-12, D-07, D-10) — the superadmin fleet console's
 * module editor. Opened as a modal from a per-row action on
 * `FleetConsolePage.tsx`, rendered ONLY when `instance.editable` is true
 * (P-46) — a `managed: external` row never mounts this component at all.
 *
 * P-45 (LOCKED): the save control is driven ONLY by the server's
 * `preview.allowed`. This client never computes its own verdict, never
 * turns the save control on ahead of a fresh server response, and offers
 * no alternate path to submit a refused change. D-10 removed the human
 * approval gate behind the triggered deploy, so this validation refusal is
 * the ONLY barrier left standing between a mis-click and a crash-looping
 * customer instance — the server refuses independently regardless
 * (`fleetSaveService.ts`), so this is defence in depth, not the only layer.
 *
 * P-47/P-48: every server-supplied string (blocking reasons, the guarantee
 * note, the save outcome message) is rendered VERBATIM as a JSX text node
 * — never paraphrased, never composed into a parallel client-authored
 * sentence, never injected as raw markup.
 *
 * P-34 guard vs P-45, reconciled (added 2026-09-01): `localCheckoutBase`
 * below is a client-side condition, which reads at first glance like a
 * contradiction of P-45's "this client never computes its own verdict". It
 * is not. The guard is SUBTRACTIVE ONLY — it can turn the save control off,
 * never on, and it neither overrides a server refusal nor creates any path
 * to submit one. P-45 exists to stop the client manufacturing permission the
 * server did not grant; a client that declines to offer a button it knows
 * cannot succeed is the same principle, not an exception to it. Any future
 * client-side condition here must satisfy that same one-way property.
 *
 * Dependency tree vs P-45 (added 2026-09-07): the checklist is now a nested
 * tree built from the server-supplied `moduleCatalog`, and a click applies
 * the dependency closure — checking a module selects what it needs,
 * unchecking one deselects what needs it. This is not the client computing a
 * verdict either. It changes the REQUEST (which modules the operator is
 * asking for); it never touches the ANSWER (whether that request may be
 * saved), which still comes only from `preview.allowed`. The preview panels
 * below are untouched and still render the server's own account of the
 * closure — with the client closure in place they normally agree, and the
 * moment they would not, the server's version is the one on screen.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Spinner } from '../common/Spinner';
import { ApiClientError } from '../../services/api/client';
import { usePreviewInstanceModules, useSaveInstanceModules } from '../../hooks/useFleetInstances';
import { MODULE_IDS } from '../../services/api/modules';
import type { ModuleId } from '../../services/api/modules';
import type {
  FleetInstanceView,
  ModuleCatalogEntry,
  ModuleChangePreview,
  SaveModulesResult,
} from '../../services/api/fleet';
import {
  buildModuleForest,
  collateralChanges,
  deselectWithDependents,
  flattenForest,
  selectWithDependencies,
  type ModuleTreeNode,
} from '../../utils/moduleGraph';

/** Milliseconds to wait after the last checkbox click before firing a preview. */
const PREVIEW_DEBOUNCE_MS = 300;

export interface FleetModuleEditorProps {
  instance: FleetInstanceView;
  baseSha: string;
  /**
   * The module registry served alongside the listing
   * (`GET /api/fleet/instances`). Drives the dependency tree AND the
   * auto-toggle closures below. An empty array is a supported degraded
   * mode: the checklist renders flat and no closure is applied, which is
   * exactly the pre-tree behaviour — the server preview still explains what
   * would happen.
   */
  moduleCatalog: readonly ModuleCatalogEntry[];
  onClose: () => void;
}

/** What the last toggle changed beyond the checkbox that was clicked. */
interface CollateralNote {
  turnedOn: ModuleId[];
  turnedOff: ModuleId[];
}

const NO_COLLATERAL: CollateralNote = { turnedOn: [], turnedOff: [] };

function isModuleId(token: string): token is ModuleId {
  return (MODULE_IDS as readonly string[]).includes(token);
}

/**
 * Label lookup, best source first: the server-supplied module catalog (every
 * module, always present), then the preview's own `autoEnabled`/
 * `blockedDisables` entries (only the modules those panels mention), then the
 * bare id. The preview fallback predates the catalog and is kept because a
 * degraded/empty catalog must still produce readable panel text.
 */
function buildLabelMap(
  catalog: readonly ModuleCatalogEntry[],
  preview: ModuleChangePreview | null
): Map<string, string> {
  const map = new Map<string, string>();
  for (const entry of catalog) map.set(entry.id, entry.label);
  if (!preview) return map;
  for (const entry of preview.autoEnabled) map.set(entry.module, entry.label);
  for (const entry of preview.blockedDisables) map.set(entry.module, entry.label);
  return map;
}

export function FleetModuleEditor({
  instance,
  baseSha,
  moduleCatalog,
  onClose,
}: FleetModuleEditorProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<ModuleId>>(
    () => new Set(instance.desiredModules.filter(isModuleId))
  );
  // Phase 108 (Plan 108-06, D-03) — the draft per-instance env values,
  // seeded from what is already committed on this instance. Cleared per key
  // (never held as an empty string) so a blanked field never reaches the
  // server as an invalid zero-length value.
  const [moduleEnvDraft, setModuleEnvDraft] = useState<Record<string, string>>(
    () => ({ ...instance.moduleEnv })
  );
  const [collateral, setCollateral] = useState<CollateralNote>(NO_COLLATERAL);
  const [debouncePending, setDebouncePending] = useState(false);
  const [displayedPreview, setDisplayedPreview] = useState<ModuleChangePreview | null>(null);
  const [saveResult, setSaveResult] = useState<SaveModulesResult | null>(null);
  const [staleManifest, setStaleManifest] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const previewMutation = usePreviewInstanceModules(instance.id);
  const saveMutation = useSaveInstanceModules(instance.id);

  // Kept in a ref so the debounce effect below only needs `selected` in its
  // dependency array — `previewMutation.mutate`'s identity is not something
  // this effect should re-run on (it can change on every mutation status
  // transition, which would defeat the debounce entirely).
  const previewMutateRef = useRef(previewMutation.mutate);
  previewMutateRef.current = previewMutation.mutate;

  // A preview fires on every CHECKBOX CHANGE (P-45's own wording) — as of
  // Plan 108-06, also on every moduleEnv field EDIT, through the SAME
  // debounced path, so the verdict shown always matches the draft. Not on
  // mount — skip the effect's first run so opening the editor does not
  // spend the debounce window (and disable Save) before the operator has
  // touched anything. The save control's own `!!displayedPreview` check
  // already keeps Save off until a verdict exists.
  const isMountRef = useRef(true);
  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    setDebouncePending(true);
    const timer = setTimeout(() => {
      setDebouncePending(false);
      const modules = MODULE_IDS.filter((id) => selected.has(id));
      previewMutateRef.current({ modules, moduleEnv: moduleEnvDraft });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [selected, moduleEnvDraft]);

  useEffect(() => {
    if (previewMutation.data) {
      setDisplayedPreview(previewMutation.data);
    }
  }, [previewMutation.data]);

  const previewPending = debouncePending || previewMutation.isPending;
  const labelMap = buildLabelMap(moduleCatalog, displayedPreview);

  function labelFor(id: string): string {
    return labelMap.get(id) ?? id;
  }

  // The forest is derived from the catalog on every render — 13 entries, and
  // memoising it would only add a dependency array to keep honest.
  const forest = buildModuleForest(moduleCatalog);
  const rows: ModuleTreeNode[] =
    forest.length > 0
      ? flattenForest(forest)
      : // Degraded mode: no catalog reached the client. Render the flat
        // checklist rather than nothing, and skip the closures — the server
        // preview remains the operator's explanation of what will happen.
        MODULE_IDS.map((id) => ({
          id,
          label: labelFor(id),
          path: id,
          depth: 0,
          alsoUnder: [],
          children: [],
        }));

  const autoToggled = new Set<ModuleId>([...collateral.turnedOn, ...collateral.turnedOff]);

  /**
   * Applies the dependency closure for one checkbox click.
   *
   * This is a SELECTION aid, not a verdict (P-45): it changes what the
   * operator is asking for, then the server re-resolves that request
   * independently and can still refuse it. Checking a module pulls in what
   * it needs; unchecking one drops what needs it — without the second half
   * the checkbox would appear to turn off while the server's forward
   * closure quietly re-added the module, which is the confusing behaviour
   * the `blockedDisables` panel exists to explain after the fact.
   */
  function toggleModule(id: ModuleId) {
    // A save outcome or a stale-manifest notice belongs to the SELECTION
    // that produced it — once the operator changes the checklist again,
    // neither is still an honest description of what would happen now.
    setSaveResult(null);
    setStaleManifest(false);
    setSaveError(null);
    // Computed from the rendered `selected` rather than inside a functional
    // updater: the updater would run twice under StrictMode and fire
    // `setCollateral` as a side effect from within a render-phase callback.
    const next = selected.has(id)
      ? deselectWithDependents(selected, id, moduleCatalog)
      : selectWithDependencies(selected, id, moduleCatalog);
    setCollateral(collateralChanges(selected, next, id, moduleCatalog));
    setSelected(next);
  }

  /**
   * Updates one moduleEnv draft key (Phase 108, Plan 108-06). An emptied
   * field REMOVES the key from the draft rather than holding a zero-length
   * string — `MODULE_ENV_ALLOWED_KEYS` values are `minLength: 1` at the
   * schema layer, so a blank field would otherwise reach the server as an
   * invalid value on the next debounced preview instead of simply "not
   * supplied yet".
   */
  function handleModuleEnvChange(key: string, value: string) {
    setSaveResult(null);
    setStaleManifest(false);
    setSaveError(null);
    setModuleEnvDraft((prev) => {
      if (value.length === 0) {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
  }

  function applyResolvedSetIfAlsoDisabled(ids: readonly string[]) {
    setSaveResult(null);
    setStaleManifest(false);
    setSaveError(null);
    setCollateral(NO_COLLATERAL);
    setSelected(new Set(ids.filter(isModuleId)));
  }

  async function handleSave() {
    if (!displayedPreview) return;
    const modules = MODULE_IDS.filter((id) => selected.has(id));
    setSaveError(null);
    setStaleManifest(false);
    try {
      const result = await saveMutation.mutateAsync({ modules, baseSha, moduleEnv: moduleEnvDraft });
      setSaveResult(result);
      setDisplayedPreview(result.preview);
    } catch (err) {
      if (err instanceof ApiClientError && err.statusCode === 409) {
        setStaleManifest(true);
        // The manifest moved underneath the operator — refetch the listing
        // so the console's own `baseSha` catches up. This component is
        // NEVER responsible for retrying the save itself; the operator must
        // close this editor, re-open it against the refreshed listing, and
        // re-apply their intended change.
        queryClient.invalidateQueries({ queryKey: ['fleet-instances'] });
      } else {
        setSaveError(err instanceof Error ? err.message : 'Failed to save the module change.');
      }
    }
  }

  // P-34 pre-submit guard. `commitTwoFiles` refuses a `baseSha` of
  // 'local-checkout' — the P-16 dev-fallback sentinel returned when the
  // manifest was read off the local filesystem instead of GitHub — because a
  // save cannot commit against a phantom base. That server-side refusal is the
  // real defence and stays. But `baseSha` already reaches the client (it is
  // `required` in `fleetInstancesResponse`), so the condition is knowable
  // BEFORE the click: without this guard the Save button renders fully
  // enabled and fails only after the request, which is a control that looks
  // actionable but cannot succeed. Surfaced as a disabled Save plus a reason
  // rather than a doomed click.
  const localCheckoutBase = !baseSha || baseSha === 'local-checkout';

  const saveEnabled =
    !!displayedPreview &&
    displayedPreview.allowed &&
    (displayedPreview.changed || displayedPreview.moduleEnvChanged) &&
    !previewPending &&
    !localCheckoutBase;

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
      {localCheckoutBase && (
        <p className="text-xs text-gray-600 text-right">
          Saving is unavailable against a local checkout — the manifest was read from the
          filesystem, not GitHub, so there is no commit to base a change on. Configure{' '}
          <code>FLEET_GITHUB_TOKEN</code> to enable saving. Previewing and validation work
          normally.
        </p>
      )}
      <Button onClick={handleSave} disabled={!saveEnabled || saveMutation.isPending} isLoading={saveMutation.isPending}>
        Save
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Edit modules: ${instance.id}`}
      size="lg"
      ariaLabelledBy="fleet-module-editor-title"
      footer={footer}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Changing modules here writes <code>ops/fleet.yaml</code> in git and triggers a targeted
          redeploy of <code>{instance.id}</code>. The new module set takes effect only once the ECS
          rolling deployment completes — expect roughly two to three minutes.
        </p>

        {/* The dependency tree. Rows are nested by dependency; a module with
            two parents (`capacity` needs both `projects` and `people`)
            appears under each of them and the duplicate rows share one
            checkbox state, because they are one module.

            `core` is rendered checked and DISABLED. That is a departure from
            the original flat checklist, which left it clickable on the
            reasoning that the server would re-add it anyway — true then, but
            with the reverse closure wired up, unchecking `core` would now
            deselect all twelve other modules in one click, and the resulting
            set is one the server refuses regardless. Offering that click
            buys nothing and costs the operator their whole selection. */}
        <div className="border rounded p-3 space-y-0.5" role="group" aria-label="Modules">
          {rows.map((row) => {
            const isCore = row.id === 'core';
            return (
              <div
                key={row.path}
                style={{ paddingLeft: `${row.depth * 1.25}rem` }}
                className={`flex items-center gap-2 rounded px-1 py-0.5 text-sm transition-colors ${
                  autoToggled.has(row.id) ? 'bg-amber-50 ring-1 ring-amber-300' : ''
                }`}
              >
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selected.has(row.id)}
                    disabled={isCore}
                    onChange={() => toggleModule(row.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-60"
                  />
                  <span className={isCore ? 'text-gray-500' : ''}>{row.label}</span>
                </label>
                {isCore && (
                  <span
                    className="text-xs text-gray-400"
                    title="Every other module depends on core, directly or transitively"
                  >
                    (always required)
                  </span>
                )}
                {row.alsoUnder.length > 0 && (
                  <span className="text-xs text-gray-400">
                    also under {row.alsoUnder.join(', ')}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* What the last click changed beyond the checkbox that was clicked.
            Without this the closure is invisible: rows several lines away
            flip and nothing says why. Cleared by the next toggle. */}
        {(collateral.turnedOn.length > 0 || collateral.turnedOff.length > 0) && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-2" role="status">
            {collateral.turnedOn.length > 0 && (
              <p>
                Also turned on:{' '}
                <span className="font-medium">
                  {collateral.turnedOn.map((id) => labelFor(id)).join(', ')}
                </span>{' '}
                — required by what you selected.
              </p>
            )}
            {collateral.turnedOff.length > 0 && (
              <p>
                Also turned off:{' '}
                <span className="font-medium">
                  {collateral.turnedOff.map((id) => labelFor(id)).join(', ')}
                </span>{' '}
                — they depend on what you turned off.
              </p>
            )}
          </div>
        )}

        {/* Per-key environment prerequisite inputs (Phase 108, Plan 108-06,
            D-03). Placed between the module checkboxes and the preview
            results so the operator fills prerequisites before reading the
            verdict. Driven by `requiredModuleEnvKeys` — the non-secret env
            keys the server says the RESOLVED module set needs — so this
            section renders nothing until a preview exists and nothing at
            all when the resolved set needs none. Plain text inputs, never
            password fields: these are non-secret by MODULE_ENV_ALLOWED_KEYS'
            own construction, and masking them would wrongly suggest this
            console handles secrets, which D-02 forbids. */}
        {displayedPreview && displayedPreview.requiredModuleEnvKeys.length > 0 && (
          <div className="border rounded p-3 space-y-2" role="group" aria-label="Environment prerequisites">
            <p className="text-xs text-gray-500">
              Only non-secret values are entered here. They are committed to{' '}
              <code>ops/fleet.yaml</code> in git, alongside the module change. Secrets are never
              entered in this console.
            </p>
            {displayedPreview.requiredModuleEnvKeys.map((key) => (
              <label key={key} className="block text-sm">
                <span className="block font-mono text-xs text-gray-700 mb-0.5">{key}</span>
                <input
                  type="text"
                  value={moduleEnvDraft[key] ?? ''}
                  onChange={(e) => handleModuleEnvChange(key, e.target.value)}
                  className="block w-full rounded border-gray-300 text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </label>
            ))}
          </div>
        )}

        {previewPending && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" />
            <span>Checking this module set against the server&apos;s validation gate&hellip;</span>
          </div>
        )}

        {previewMutation.isError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" role="alert">
            Could not compute a preview:{' '}
            {previewMutation.error instanceof Error ? previewMutation.error.message : 'unknown error'}
          </p>
        )}

        {displayedPreview && (
          <div className="space-y-3">
            {/* 1. Blocking reasons. */}
            {!displayedPreview.allowed && (
              <div className="border border-red-300 bg-red-50 rounded p-3" role="alert">
                <p className="text-sm font-semibold text-red-800">
                  This module set cannot be saved.
                </p>
                <ul className="mt-1 space-y-1 text-sm text-red-700 list-disc list-inside">
                  {displayedPreview.blocking.map((b, idx) => (
                    <li key={`${b.rule}-${idx}`}>
                      <span className="font-mono text-xs">[{b.rule}]</span> {b.message}
                      {/* Plan 108-07 (D-05/D-06, MODULE-PREREQ-03) — how-to-obtain
                          guidance and the exact command, rendered verbatim from
                          docs/private/CONFIG.md, never composed or reworded here. Renders
                          nothing when both are null (every reason other than
                          missing-module-env). */}
                      {(b.howToObtain !== null || b.command !== null) && (
                        <div className="mt-1 pl-4 text-xs text-red-700 space-y-0.5">
                          {b.howToObtain !== null && (
                            <p>
                              <span className="font-semibold">How to obtain:</span> {b.howToObtain}
                            </p>
                          )}
                          {b.command !== null && (
                            <p>
                              <span className="font-semibold">Command:</span> <code>{b.command}</code>
                            </p>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-red-600">
                  These reasons cannot be bypassed from this screen. The save endpoint reaches the
                  same verdict independently, so there is no alternate path to submit this set.
                </p>
              </div>
            )}

            {/* 2. Auto-enabled dependencies. */}
            {displayedPreview.autoEnabled.length > 0 && (
              <div className="border border-amber-300 bg-amber-50 rounded p-3">
                <p className="text-sm font-semibold text-amber-800">
                  These modules will also turn ON:
                </p>
                <ul className="mt-1 space-y-1 text-sm text-amber-800 list-disc list-inside">
                  {displayedPreview.autoEnabled.map((a) => (
                    <li key={a.module}>
                      <span className="font-medium">{a.label}</span> — required by:{' '}
                      {a.requiredBy.map((r) => labelFor(r)).join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 3. Blocked disables — the single most important panel on this
                screen: without the one-click alternative, unchecking a
                module with a remaining dependent silently does nothing,
                because the server's forward closure re-adds it. */}
            {displayedPreview.blockedDisables.length > 0 && (
              <div className="border border-orange-300 bg-orange-50 rounded p-3">
                <p className="text-sm font-semibold text-orange-800">
                  Turning these off does not take effect on their own:
                </p>
                <ul className="mt-1 space-y-2 text-sm text-orange-800">
                  {displayedPreview.blockedDisables.map((entry) => (
                    <li key={entry.module}>
                      <p>
                        Turning off <span className="font-medium">{entry.label}</span> also
                        requires turning off:{' '}
                        {entry.blockedBy.map((b) => labelFor(b)).join(', ') || '(nothing else selected)'}
                        . Otherwise it stays enabled — the server re-adds it as a dependency.
                      </p>
                      <button
                        type="button"
                        onClick={() => applyResolvedSetIfAlsoDisabled(entry.resolvedSetIfAlsoDisabled)}
                        className="mt-1 text-xs px-2 py-1 rounded border border-orange-400 text-orange-800 hover:bg-orange-100"
                      >
                        Turn all of these off together
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 4. Live environment advisory — advisory only, never gating. */}
            {displayedPreview.liveEnvAdvisory && (
              <div className="border border-blue-200 bg-blue-50 rounded p-3">
                <p className="text-sm font-semibold text-blue-800">
                  <span className="uppercase text-xs tracking-wide mr-1">Advisory</span>
                  Live environment comparison — never part of the pass/fail decision above.
                </p>
                {!displayedPreview.liveEnvAdvisory.available ? (
                  <p className="mt-1 text-sm text-blue-700">
                    Not available: {displayedPreview.liveEnvAdvisory.reason}
                  </p>
                ) : (
                  <div className="mt-1 text-sm text-blue-700 space-y-1">
                    <p>
                      Present on the live task definition:{' '}
                      {displayedPreview.liveEnvAdvisory.presentNames.join(', ') || '(none)'}
                    </p>
                    <p>
                      Absent on the live task definition:{' '}
                      {displayedPreview.liveEnvAdvisory.absentRequiredNames.join(', ') || '(none)'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* 5. Guarantee note — rendered verbatim in EVERY verdict, allowed or refused. */}
            <div className="border border-gray-300 bg-gray-50 rounded p-3">
              <p className="text-xs text-gray-700">{displayedPreview.guaranteeNote}</p>
            </div>

            {/* 6. Diff summary. */}
            <div className="text-xs text-gray-500">
              <span className="font-mono">{displayedPreview.currentRaw}</span>
              {' → '}
              <span className="font-mono">{displayedPreview.nextRaw}</span>
            </div>
          </div>
        )}

        {staleManifest && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-2" role="alert">
            The fleet manifest changed since this page loaded. Close this editor, let the console
            refresh, then re-open it and re-apply your intended change.
          </p>
        )}

        {saveError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" role="alert">
            {saveError}
          </p>
        )}

        {saveResult && saveResult.outcome === 'refused' && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" role="alert">
            {saveResult.message}
          </p>
        )}

        {saveResult && saveResult.outcome === 'committed' && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-2" role="alert">
            {saveResult.message}
          </p>
        )}

        {saveResult && saveResult.outcome === 'committed_and_triggered' && (
          <div className="text-sm text-blue-800 bg-blue-50 border border-blue-300 rounded px-3 py-2" role="status">
            <p>{saveResult.message}</p>
            {saveResult.pipelineNumber !== null && (
              <p className="mt-1 text-xs">Pipeline #{saveResult.pipelineNumber} — rollout PENDING.</p>
            )}
            <button
              type="button"
              onClick={onClose}
              className="mt-2 text-xs px-2 py-1 rounded border border-blue-400 text-blue-800 hover:bg-blue-100"
            >
              Acknowledge
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
